import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { CheckCircle2, AlertTriangle, Download, Package, RefreshCw, Trash2 } from "lucide-react";
import { payrollOpsService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { DsButton, DsAlert } from "../../../design-system";

/**
 * Payroll Settings → Compliance Updates
 * Two salary regimes only (Form 24Q ≤ FY 2025-26, Form 138 ≥ TY 2026-27).
 * One Install/Update wizard: FVU zip + bundled RPU (+ optional advanced RPU zip).
 */
export default function TdsEngineComplianceTab({
  darkMode = false,
  canEdit = true,
  title = "Salary TDS — Compliance Updates",
}) {
  const { hasScreenAccess } = usePermissions() || {};
  const canView =
    typeof hasScreenAccess !== "function" ||
    hasScreenAccess("payroll", "tds_engine_compliance", "view");
  const canManage =
    canEdit &&
    (typeof hasScreenAccess !== "function" ||
      hasScreenAccess("payroll", "tds_engine_compliance", "edit"));

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [wizardRegime, setWizardRegime] = useState(null);
  const [fvuVersion, setFvuVersion] = useState("");
  const [useBundled, setUseBundled] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customLayoutKey, setCustomLayoutKey] = useState("");
  const [allowReplace, setAllowReplace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollOpsService.getTdsSalaryRegimes();
      setStatus(res?.data || res || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load salary TDS packs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  if (!canView) {
    return (
      <DsAlert tone="warning">
        You do not have access to TDS Engine / Compliance Updates.
      </DsAlert>
    );
  }

  const openWizard = (card) => {
    setWizardRegime(card);
    setFvuVersion(card.installedFvuVersion || card.recommendedFvuVersion || "");
    setUseBundled(true);
    setShowAdvanced(false);
    setCustomLayoutKey("");
    setAllowReplace(false);
  };

  const closeWizard = () => {
    setWizardRegime(null);
    setSaving(false);
  };

  const submitInstall = async (e) => {
    e.preventDefault();
    if (!canManage || !wizardRegime) return;
    const form = e.target;
    const fvuInput = form.elements.fvuZip;
    const fvuFile = fvuInput?.files?.[0];
    if (!fvuFile) {
      toast.error("Select the Protean FVU zip file");
      return;
    }
    const fd = new FormData();
    fd.append("regimeKey", wizardRegime.key);
    fd.append("fvuZip", fvuFile);
    if (fvuVersion.trim()) fd.append("fvuUtilityVersion", fvuVersion.trim());
    fd.append("useBundledSpec", useBundled ? "true" : "false");
    fd.append("allowReplaceFvu", allowReplace ? "true" : "false");
    if (!useBundled) {
      const rpuFile = form.elements.rpuZip?.files?.[0];
      if (!rpuFile) {
        toast.error("Advanced mode: upload an RPU pack zip, or switch back to bundled RPU");
        return;
      }
      fd.append("rpuZip", rpuFile);
      if (customLayoutKey.trim()) fd.append("customLayoutKey", customLayoutKey.trim());
    }

    setSaving(true);
    try {
      const res = await payrollOpsService.installTdsSalaryRegime(fd);
      const ver = res?.data?.fvuUtilityVersion || fvuVersion;
      toast.success(`${wizardRegime.title}: FVU ${ver} installed and activated`);
      closeWizard();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Install failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!canManage) return;
    const ok = window.confirm(
      "Clear all TDS engine version rows and junk FVU folders?\n\n" +
        "Pinned versions used by existing filings are only deactivated (not deleted).\n" +
        "Bundled RPU specs are kept. You will Install Card A and Card B again."
    );
    if (!ok) return;
    setResetting(true);
    try {
      const res = await payrollOpsService.resetTdsSalaryEngine({ deleteJunkFvuFolders: true });
      toast.success(res?.data?.message || "Catalog cleared — install both cards");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const cards = status?.cards || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold mb-1">{title}</h3>
          <p className="ds-caption text-[hsl(var(--ds-muted-fg))] max-w-3xl">
            Install official Protean FVU packs for salary returns only (Form 24Q up to FY 2025-26,
            Form No. 138 from TY 2026-27). One zip install registers FVU + bundled RPU + DB rows.
            Engine root: <code className="text-xs">{status?.engineRoot || "…"}</code>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DsButton type="button" variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </DsButton>
          {canManage ? (
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={resetting}
            >
              <Trash2 size={14} />
              {resetting ? "Clearing…" : "Fresh start"}
            </DsButton>
          ) : null}
        </div>
      </div>

      {status?.hiddenNonSalaryCount > 0 ? (
        <DsAlert tone="info">
          {status.hiddenNonSalaryCount} non-salary engine row(s) (26Q / 27Q / 27EQ) are hidden here.
          Use Fresh start to clear junk rows if you want a clean salary catalog.
        </DsAlert>
      ) : null}

      {loading && !cards.length ? (
        <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Loading…</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {cards.map((card) => (
            <div
              key={card.key}
              className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-semibold text-[hsl(var(--ds-fg))]">{card.title}</h4>
                  <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">{card.subtitle}</p>
                </div>
                <StatusPill ready={card.ready} label={card.statusLabel} />
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <dt className="text-[hsl(var(--ds-muted-fg))]">Form</dt>
                  <dd className="font-medium">{card.formLabel}</dd>
                </div>
                <div>
                  <dt className="text-[hsl(var(--ds-muted-fg))]">Recommended FVU</dt>
                  <dd className="font-mono font-medium">{card.recommendedFvuVersion}</dd>
                </div>
                <div>
                  <dt className="text-[hsl(var(--ds-muted-fg))]">Installed FVU</dt>
                  <dd className="font-mono font-medium">
                    {card.installedFvuVersion || "—"}
                    {card.jarExists ? " ✓" : card.installedFvuVersion ? " ✗ jar" : ""}
                  </dd>
                </div>
                <div>
                  <dt className="text-[hsl(var(--ds-muted-fg))]">RPU layouts</dt>
                  <dd className="font-medium">
                    {(card.activeVersions || []).filter((v) => v.specExists && v.jarExists).length}/
                    {(card.bundledSpecs || []).length} ready
                  </dd>
                </div>
              </dl>

              <ul className="mt-3 space-y-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                {(card.bundledSpecs || []).map((s) => {
                  const row = (card.activeVersions || []).find(
                    (v) =>
                      String(v.quarterScope).replace(/\s/g, "") ===
                      String(s.quarterScope).replace(/\s/g, "")
                  );
                  const ok = row?.jarExists && row?.specExists;
                  return (
                    <li key={s.rpuSpecKey} className="flex gap-2">
                      {ok ? (
                        <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-emerald-600" />
                      ) : (
                        <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-600" />
                      )}
                      <span>
                        {s.label}: <code>{s.rpuSpecKey}</code>
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4 flex flex-wrap gap-2">
                {canManage ? (
                  <DsButton type="button" variant="primary" size="sm" onClick={() => openWizard(card)}>
                    <Package size={14} />
                    {card.ready ? "Update pack" : "Install / Update"}
                  </DsButton>
                ) : null}
                <a
                  href={card.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-8 items-center gap-1 rounded-md border border-[hsl(var(--ds-border))] px-3 text-xs font-semibold text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                >
                  <Download size={14} />
                  Get FVU from Protean
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] p-4 text-xs leading-relaxed text-[hsl(var(--ds-muted-fg))]">
        <p className="font-semibold text-[hsl(var(--ds-fg))]">How to install</p>
        <ol className="mt-2 ml-4 list-decimal space-y-1">
          <li>Download the official standalone FVU zip from Protean TIN (link on each card).</li>
          <li>Click Install / Update on the matching card — upload the zip (no manual extract).</li>
          <li>Keep “Use bundled RPU” on unless PaceHRM gave you a new RPU pack for a format change.</li>
          <li>
            When both cards show Ready to file, open Payroll → Form 24Q / Form 138 filing for the
            quarter.
          </li>
        </ol>
      </div>

      {wizardRegime ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className="w-full max-w-lg rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tds-install-title"
          >
            <h4 id="tds-install-title" className="text-base font-semibold">
              Install — {wizardRegime.title}
            </h4>
            <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
              {wizardRegime.formLabel} · recommended FVU {wizardRegime.recommendedFvuVersion}
            </p>

            <form onSubmit={submitInstall} className="mt-4 space-y-3">
              <label className="block text-sm">
                Protean FVU zip *
                <input
                  name="fvuZip"
                  type="file"
                  accept=".zip,application/zip"
                  required
                  className="mt-1 block w-full text-sm"
                />
              </label>

              <label className="block text-sm">
                Version label
                <input
                  className="mt-1 block w-full rounded-lg border border-[hsl(var(--ds-border))] bg-transparent px-2 py-1.5"
                  value={fvuVersion}
                  onChange={(e) => setFvuVersion(e.target.value)}
                  placeholder={wizardRegime.recommendedFvuVersion}
                />
                <span className="mt-0.5 block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                  Leave blank to auto-detect from the jar name inside the zip. Use a new label (e.g.
                  9.6) when Protean releases an upgrade — packs are never overwritten.
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={useBundled}
                  onChange={(e) => {
                    setUseBundled(e.target.checked);
                    if (e.target.checked) setShowAdvanced(false);
                  }}
                />
                <span>
                  <span className="font-medium">Use bundled RPU spec (recommended)</span>
                  <span className="block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    Uses PaceHRM product specs for this regime. Normal Protean FVU-only updates need
                    nothing else.
                  </span>
                </span>
              </label>

              <button
                type="button"
                className="text-[11px] underline text-[hsl(var(--ds-muted-fg))]"
                onClick={() => {
                  setShowAdvanced((v) => !v);
                  if (!showAdvanced) setUseBundled(false);
                }}
              >
                {showAdvanced ? "Hide advanced RPU upload" : "Advanced: custom RPU pack (rare)"}
              </button>

              {showAdvanced && !useBundled ? (
                <div className="space-y-2 rounded-lg border border-[hsl(var(--ds-border))] p-3">
                  <label className="block text-sm">
                    RPU pack zip (must include specification.json)
                    <input
                      name="rpuZip"
                      type="file"
                      accept=".zip,application/zip"
                      className="mt-1 block w-full text-sm"
                    />
                  </label>
                  <label className="block text-sm">
                    Layout key
                    <input
                      className="mt-1 block w-full rounded-lg border border-[hsl(var(--ds-border))] bg-transparent px-2 py-1.5"
                      value={customLayoutKey}
                      onChange={(e) => setCustomLayoutKey(e.target.value)}
                      placeholder="q1q3-v1.1"
                    />
                  </label>
                </div>
              ) : null}

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={allowReplace}
                  onChange={(e) => setAllowReplace(e.target.checked)}
                />
                <span className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                  Replace existing <code>fvu-{"{version}"}</code> folder if the same version label
                  already exists (use carefully). Prefer a new version label instead.
                </span>
              </label>

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <DsButton type="button" variant="outline" size="sm" onClick={closeWizard} disabled={saving}>
                  Cancel
                </DsButton>
                <DsButton type="submit" variant="primary" size="sm" disabled={saving}>
                  {saving ? "Installing…" : "Save pack"}
                </DsButton>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StatusPill({ ready, label }) {
  return (
    <span
      className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        ready
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-amber-200 bg-amber-50 text-amber-900"
      }`}
    >
      {label}
    </span>
  );
}
